/**
 * MEVIA v5.0 - People Analytics Dashboard
 * Replaces EvolutionImpact.js (D4 Issue #16)
 * 
 * 6 Blocks:
 * 1. Demographics (gender, age, department distribution)
 * 2. Tenure & Retention (avg tenure, turnover, benchmarks)
 * 3. Capital Heatmap (10 MetaImpact capitals by department)
 * 4. Personality Aggregate (Enneagram distribution)
 * 5. Action Plan Effectiveness
 * 6. Retention ROI
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Users, TrendingUp, TrendingDown, BarChart3, Target, DollarSign,
  Loader2, RefreshCw, Building2, Clock, Brain, Flame, Award,
  ArrowRight, CheckCircle2, AlertTriangle, PieChart, Activity, ShieldAlert
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import logger from '../utils/logger';


const TEXTS = {
  es: {
    title: 'People Analytics',
    subtitle: 'Inteligencia organizacional basada en datos',
    refresh: 'Actualizar',
    loading: 'Cargando datos...',
    noData: 'No hay datos disponibles',
    
    tabDemographics: 'Demografia',
    tabTenure: 'Permanencia',
    tabHeatmap: 'Mapa de Calor',
    tabPersonality: 'Personalidad',
    tabPlans: 'Efectividad Planes',
    tabRoi: 'ROI Retencion',
    
    // Demographics
    totalEmployees: 'Empleados Totales',
    avgAge: 'Edad Promedio',
    genderDist: 'Distribucion por Genero',
    deptDist: 'Distribucion por Departamento',
    male: 'Masculino',
    female: 'Femenino',
    other: 'Otro',
    notSpecified: 'Sin especificar',
    
    // Tenure
    avgTenure: 'Antiguedad Promedio',
    turnoverRate: 'Tasa de Rotacion',
    retentionRate: 'Tasa de Retencion',
    years: 'anios',
    months: 'meses',
    tenureBands: 'Distribucion por Antiguedad',
    lessThan1: '< 1 anio',
    oneToThree: '1-3 anios',
    threeToFive: '3-5 anios',
    fiveToTen: '5-10 anios',
    moreThan10: '10+ anios',
    
    // Heatmap
    heatmapTitle: 'Mapa de Calor de Capitales',
    heatmapDesc: 'Niveles promedio por departamento',
    low: 'Bajo',
    medium: 'Medio',
    high: 'Alto',
    
    // Personality
    personalityTitle: 'Perfil de Personalidad',
    personalityDesc: 'Distribucion de tipos de Eneagrama',
    dominantType: 'Tipo Dominante',
    
    // Plan effectiveness
    plansTitle: 'Efectividad de Planes de Accion',
    plansDesc: 'Impacto medido en evaluaciones',
    totalPlans: 'Planes Creados',
    completedPlans: 'Planes Completados',
    avgCompletion: 'Completitud Promedio',
    impactScore: 'Impacto en Score',
    
    // ROI
    roiTitle: 'ROI de Retencion',
    roiDesc: 'Retorno de inversion en talento',
    estimatedSavings: 'Ahorro Estimado',
    costPerHire: 'Costo por Contratacion',
    retainedEmployees: 'Empleados Retenidos',
    roiMultiplier: 'Multiplicador ROI',
    
    tabPulse: 'Pulso MEVIA',
    pulseTitle: 'Tendencias del Pulso MEVIA',

    // NOM-035 x Cultura tab
    tabNomCultura: 'NOM x Cultura',
    nomCulturaTitle: 'Correlacion NOM-035 x Dimensiones MEVIA',
    nomCulturaDesc: 'Mapeo oficial de dominios de riesgo psicosocial a Dimensiones culturales',
    nomCulturaDoubleSignals: 'Seniales Dobles activas',
    nomCulturaDomains: 'Dominios evaluados',
    nomCulturaGap: 'Brecha',
    nomCulturaRisk: 'Riesgo NOM',
    nomCulturaDimensions: 'Dimensiones',
    nomCulturaDoubleSignalTag: 'Senial Doble',
    nomCulturaExecutiveSummary: 'Resumen Ejecutivo',
    nomCulturaModulesRequired: 'Los modulos NOM-035 y Cultura deben estar activos para ver esta seccion.',
    nomCulturaNoData: 'No hay datos. Aplique la Guia II/III y complete al menos una evaluacion de Dimensiones.',
    pulseDesc: 'Evolución del IPM y dimensiones entre períodos',
    ipmTrend: 'Tendencia IPM',
    participationTrend: 'Tendencia de Participación',
    dimensionMovements: 'Movimiento de Dimensiones',
    mostImproved: 'Más mejorados',
    mostDeclined: 'Más deteriorados',
    periods: 'períodos',
    improved: 'Mejoró',
    declined: 'Empeoró',
    noPulseData: 'Sin datos de tendencias de pulso',
    overallDelta: 'Cambio General IPM'
  },
  en: {
    title: 'People Analytics',
    subtitle: 'Data-driven organizational intelligence',
    refresh: 'Refresh',
    loading: 'Loading data...',
    noData: 'No data available',
    
    tabDemographics: 'Demographics',
    tabTenure: 'Tenure',
    tabHeatmap: 'Heat Map',
    tabPersonality: 'Personality',
    tabPlans: 'Plan Effectiveness',
    tabRoi: 'Retention ROI',
    
    totalEmployees: 'Total Employees',
    avgAge: 'Average Age',
    genderDist: 'Gender Distribution',
    deptDist: 'Department Distribution',
    male: 'Male',
    female: 'Female',
    other: 'Other',
    notSpecified: 'Not specified',
    
    avgTenure: 'Average Tenure',
    turnoverRate: 'Turnover Rate',
    retentionRate: 'Retention Rate',
    years: 'years',
    months: 'months',
    tenureBands: 'Tenure Distribution',
    lessThan1: '< 1 year',
    oneToThree: '1-3 years',
    threeToFive: '3-5 years',
    fiveToTen: '5-10 years',
    moreThan10: '10+ years',
    
    heatmapTitle: 'Capital Heat Map',
    heatmapDesc: 'Average levels by department',
    low: 'Low',
    medium: 'Medium',
    high: 'High',
    
    personalityTitle: 'Personality Profile',
    personalityDesc: 'Enneagram type distribution',
    dominantType: 'Dominant Type',
    
    plansTitle: 'Action Plan Effectiveness',
    plansDesc: 'Impact measured in evaluations',
    totalPlans: 'Plans Created',
    completedPlans: 'Plans Completed',
    avgCompletion: 'Avg Completion',
    impactScore: 'Score Impact',
    
    roiTitle: 'Retention ROI',
    roiDesc: 'Return on talent investment',
    estimatedSavings: 'Estimated Savings',
    costPerHire: 'Cost per Hire',
    retainedEmployees: 'Retained Employees',
    roiMultiplier: 'ROI Multiplier',
    
    tabPulse: 'MEVIA Pulse',
    pulseTitle: 'MEVIA Pulse Trends',

    // NOM-035 x Cultura tab
    tabNomCultura: 'NOM x Culture',
    nomCulturaTitle: 'NOM-035 x MEVIA Dimensions Correlation',
    nomCulturaDesc: 'Official mapping of psychosocial risk domains to cultural Dimensions',
    nomCulturaDoubleSignals: 'Active Double Signals',
    nomCulturaDomains: 'Domains evaluated',
    nomCulturaGap: 'Gap',
    nomCulturaRisk: 'NOM Risk',
    nomCulturaDimensions: 'Dimensions',
    nomCulturaDoubleSignalTag: 'Double Signal',
    nomCulturaExecutiveSummary: 'Executive Summary',
    nomCulturaModulesRequired: 'NOM-035 and Culture modules must be active to view this section.',
    nomCulturaNoData: 'No data. Apply Guide II/III and complete at least one Dimensions evaluation.',
    pulseDesc: 'IPM and dimension evolution across periods',
    ipmTrend: 'IPM Trend',
    participationTrend: 'Participation Trend',
    dimensionMovements: 'Dimension Movements',
    mostImproved: 'Most improved',
    mostDeclined: 'Most declined',
    periods: 'periods',
    improved: 'Improved',
    declined: 'Declined',
    noPulseData: 'No pulse trend data available',
    overallDelta: 'Overall IPM Change'
  }
};

const CAPITAL_LABELS = {
  es: {
    FISICO: 'Fisico', HUMANO: 'Humano', MANUFACTURADO: 'Herramientas',
    FINANCIERO: 'Financiero', NATURAL: 'Natural', CULTURAL: 'Cultural',
    SOCIAL: 'Social', CONOCIMIENTO: 'Conocim.', PSICOLOGICO: 'Psicolog.',
    PROPOSITO: 'Proposito'
  },
  en: {
    FISICO: 'Physical', HUMANO: 'Human', MANUFACTURADO: 'Tools',
    FINANCIERO: 'Financial', NATURAL: 'Natural', CULTURAL: 'Cultural',
    SOCIAL: 'Social', CONOCIMIENTO: 'Knowledge', PSICOLOGICO: 'Psychol.',
    PROPOSITO: 'Purpose'
  }
};

const ENNEAGRAM_TYPES = {
  1: { name: 'Reformador', color: '#3b82f6' },
  2: { name: 'Ayudador', color: '#10b981' },
  3: { name: 'Triunfador', color: '#f59e0b' },
  4: { name: 'Individualista', color: '#8b5cf6' },
  5: { name: 'Investigador', color: '#06b6d4' },
  6: { name: 'Leal', color: '#6366f1' },
  7: { name: 'Entusiasta', color: '#f97316' },
  8: { name: 'Desafiador', color: '#ef4444' },
  9: { name: 'Pacificador', color: '#22c55e' }
};

// KPI Card
const KPICard = ({ icon: Icon, label, value, subtitle, color = 'blue', trend }) => (
  <Card data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <CardContent className="pt-5 pb-4">
      <div className="flex items-start justify-between">
        <div className={`p-2 rounded-lg bg-${color}-100`}>
          <Icon className={`w-5 h-5 text-${color}-600`} />
        </div>
        {trend !== undefined && (
          <Badge variant={trend >= 0 ? 'default' : 'destructive'} className="text-xs">
            {trend >= 0 ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
            {Math.abs(trend)}%
          </Badge>
        )}
      </div>
      <p className="mt-3 text-2xl font-bold">{value}</p>
      <p className="text-sm font-medium text-gray-700">{label}</p>
      {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
    </CardContent>
  </Card>
);

// Horizontal Bar
const HBar = ({ label, value, max, color = '#3b82f6' }) => {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="flex items-center gap-3 py-1.5">
      <span className="text-sm text-gray-600 w-28 truncate">{label}</span>
      <div className="flex-1 bg-gray-100 rounded-full h-5 relative overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
      <span className="text-sm font-medium w-10 text-right">{value}</span>
    </div>
  );
};

// Heatmap Cell
const HeatCell = ({ value }) => {
  const intensity = Math.min(value / 100, 1);
  const r = Math.round(255 - intensity * 200);
  const g = Math.round(100 + intensity * 155);
  const b = Math.round(100);
  const bg = `rgb(${r}, ${g}, ${b})`;
  const textColor = intensity > 0.5 ? '#fff' : '#374151';
  
  return (
    <td className="px-2 py-1.5 text-center text-xs font-medium border border-gray-200" 
        style={{ backgroundColor: bg, color: textColor }}>
      {value > 0 ? value.toFixed(0) : '-'}
    </td>
  );
};

export default function PeopleAnalytics({ empresaId }) {
  const { language } = useLanguage();
  const { hasAccess } = useSubscription();
  const t = TEXTS[language] || TEXTS.es;
  const capLabels = CAPITAL_LABELS[language] || CAPITAL_LABELS.es;
  
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [benchmark, setBenchmark] = useState(null);
  const [activeTab, setActiveTab] = useState('demographics');
  const [pulseTrends, setPulseTrends] = useState(null);
  const [nomCulturaData, setNomCulturaData] = useState(null);

  const showNomCultura = hasAccess('nom035') && hasAccess('culture');
  
  const loadData = useCallback(async () => {
    if (!empresaId) return;
    try {
      setLoading(true);
      const [analyticsRes, benchmarkRes] = await Promise.all([
        axios.get(`/v5/analytics/${empresaId}`),
        axios.get(`/v5/analytics/benchmark/${empresaId}?metrica=rotacion_anual`).catch(() => null)
      ]);
      if (analyticsRes.data.success) {
        setData(analyticsRes.data.data);
      }
      if (benchmarkRes?.data?.success) {
        setBenchmark(benchmarkRes.data.data);
      }
      // Load pulse trends
      try {
        const pulseRes = await axios.get(`/v5/pulso/${empresaId}/m13/trends?lang=${language}`);
        if (pulseRes.data.success) setPulseTrends(pulseRes.data.data);
      } catch (_) { /* Pulse data optional */ }
      // Load NOM-035 × Cultura correlation (R12)
      if (showNomCultura) {
        try {
          const r12Res = await axios.get(`/v5/nom035/reports/${empresaId}/r12-correlacion?lang=${language}`);
          if (r12Res.data.success) setNomCulturaData(r12Res.data.data);
        } catch (_) { /* R12 data optional — modules may not have data yet */ }
      }
    } catch (error) {
      logger.error('Error loading people analytics:', error);
      toast.error('Error loading analytics data');
    } finally {
      setLoading(false);
    }
  }, [empresaId]);
  
  useEffect(() => {
    loadData();
  }, [loadData]);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]" data-testid="people-analytics-loading">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
        <span className="ml-3 text-gray-500">{t.loading}</span>
      </div>
    );
  }
  
  if (!data) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">{t.noData}</p>
        </CardContent>
      </Card>
    );
  }
  
  const { demographics, tenure, heatmap, personality, plans, roi } = data;

  const riskColors = { healthy: '#22c55e', moderate: '#f59e0b', high_risk: '#f97316', critical: '#ef4444' };
  const riskLabels = language === 'es' 
    ? { healthy: 'Saludable', moderate: 'Moderado', high_risk: 'Alto Riesgo', critical: 'Critico' }
    : { healthy: 'Healthy', moderate: 'Moderate', high_risk: 'High Risk', critical: 'Critical' };
  
  return (
    <div className="space-y-6" data-testid="people-analytics-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">{t.title}</h2>
          <p className="text-sm text-gray-500">{t.subtitle}</p>
        </div>
        <Button variant="outline" size="sm" onClick={loadData} data-testid="refresh-analytics-btn">
          <RefreshCw className="w-4 h-4 mr-2" />
          {t.refresh}
        </Button>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={`grid ${showNomCultura ? 'grid-cols-8' : 'grid-cols-7'} w-full`}>
          <TabsTrigger value="demographics" className="text-xs" data-testid="tab-demographics">{t.tabDemographics}</TabsTrigger>
          <TabsTrigger value="tenure" className="text-xs" data-testid="tab-tenure">{t.tabTenure}</TabsTrigger>
          <TabsTrigger value="heatmap" className="text-xs" data-testid="tab-heatmap">{t.tabHeatmap}</TabsTrigger>
          <TabsTrigger value="personality" className="text-xs" data-testid="tab-personality">{t.tabPersonality}</TabsTrigger>
          <TabsTrigger value="plans" className="text-xs" data-testid="tab-plans">{t.tabPlans}</TabsTrigger>
          <TabsTrigger value="roi" className="text-xs" data-testid="tab-roi">{t.tabRoi}</TabsTrigger>
          <TabsTrigger value="pulse" className="text-xs" data-testid="tab-pulse">{t.tabPulse}</TabsTrigger>
          {showNomCultura && (
            <TabsTrigger value="nomCultura" className="text-xs" data-testid="tab-nom-cultura">{t.tabNomCultura}</TabsTrigger>
          )}
        </TabsList>
        
        {/* Block 1: Demographics */}
        <TabsContent value="demographics" className="space-y-4" data-testid="demographics-content">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KPICard icon={Users} label={t.totalEmployees} value={demographics?.total_employees || 0} color="blue" />
            <KPICard icon={Clock} label={t.avgAge} value={demographics?.avg_age ? `${demographics.avg_age.toFixed(1)}` : 'N/A'} color="purple" />
            <KPICard icon={Building2} label={language === 'es' ? 'Departamentos' : 'Departments'} value={demographics?.department_count || 0} color="teal" />
            <KPICard icon={Activity} label={language === 'es' ? 'Evaluados' : 'Evaluated'} value={demographics?.evaluated_count || 0} color="green" />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">{t.genderDist}</CardTitle>
              </CardHeader>
              <CardContent>
                {demographics?.gender_distribution && Object.keys(demographics.gender_distribution).length > 0 ? (
                  <div className="space-y-1">
                    {Object.entries(demographics.gender_distribution).map(([key, val]) => (
                      <HBar key={key} label={t[key] || key} value={val} max={demographics.total_employees} 
                            color={key === 'male' ? '#3b82f6' : key === 'female' ? '#ec4899' : '#9ca3af'} />
                    ))}
                  </div>
                ) : <p className="text-gray-400 text-sm">{t.noData}</p>}
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">{t.deptDist}</CardTitle>
              </CardHeader>
              <CardContent>
                {demographics?.department_distribution && Object.keys(demographics.department_distribution).length > 0 ? (
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {Object.entries(demographics.department_distribution)
                      .sort((a, b) => b[1] - a[1])
                      .map(([dept, count]) => (
                        <HBar key={dept} label={dept} value={count} max={demographics.total_employees} color="#14b8a6" />
                      ))}
                  </div>
                ) : <p className="text-gray-400 text-sm">{t.noData}</p>}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        {/* Block 2: Tenure & Permanence */}
        <TabsContent value="tenure" className="space-y-4" data-testid="tenure-content">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <KPICard icon={Clock} label={t.avgTenure} 
                     value={tenure?.avg_tenure_months ? `${(tenure.avg_tenure_months / 12).toFixed(1)} ${t.years}` : 'N/A'} color="blue" />
            <KPICard icon={TrendingDown} label={t.turnoverRate} 
                     value={tenure?.turnover_rate !== undefined ? `${tenure.turnover_rate.toFixed(1)}%` : 'N/A'} color="red" />
            <KPICard icon={TrendingUp} label={t.retentionRate} 
                     value={tenure?.retention_rate !== undefined ? `${tenure.retention_rate.toFixed(1)}%` : 'N/A'} color="green" />
          </div>
          
          {/* Industry Benchmark Gauge */}
          {benchmark && (
            <Card data-testid="benchmark-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">
                  {language === 'es' ? 'Benchmark de Industria' : 'Industry Benchmark'}
                  <Badge variant="outline" className="ml-2 text-xs">{language === 'es' ? benchmark.industry_name_es : benchmark.industry_name_en}</Badge>
                </CardTitle>
                <CardDescription>
                  {language === 'es' 
                    ? `Fuente: ${benchmark.fuente} (${benchmark.ano_referencia})`
                    : `Source: ${benchmark.fuente} (${benchmark.ano_referencia})`}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="relative h-8 bg-gray-100 rounded-full overflow-hidden">
                  {/* Colored zones */}
                  <div className="absolute inset-0 flex">
                    <div className="h-full bg-green-200" style={{ width: `${(benchmark.p25/Math.max(benchmark.p75*1.3, benchmark.company_value*1.3 || 50))*100}%` }} />
                    <div className="h-full bg-yellow-200" style={{ width: `${((benchmark.p50-benchmark.p25)/Math.max(benchmark.p75*1.3, benchmark.company_value*1.3 || 50))*100}%` }} />
                    <div className="h-full bg-orange-200" style={{ width: `${((benchmark.p75-benchmark.p50)/Math.max(benchmark.p75*1.3, benchmark.company_value*1.3 || 50))*100}%` }} />
                    <div className="h-full bg-red-200 flex-1" />
                  </div>
                  {/* Company marker */}
                  {benchmark.company_value !== null && (
                    <div className="absolute top-0 bottom-0 w-1 bg-blue-600 z-10"
                         style={{ left: `${Math.min((benchmark.company_value / Math.max(benchmark.p75*1.3, benchmark.company_value*1.3 || 50))*100, 98)}%` }}>
                      <div className="absolute -top-5 left-1/2 -translate-x-1/2 text-xs font-bold text-blue-600 whitespace-nowrap">
                        {benchmark.company_value.toFixed(1)}%
                      </div>
                    </div>
                  )}
                  {/* Percentile markers */}
                  {[{val: benchmark.p25, label: 'P25'}, {val: benchmark.p50, label: 'P50'}, {val: benchmark.p75, label: 'P75'}].map(m => (
                    <div key={m.label} className="absolute top-0 bottom-0 border-l border-dashed border-gray-400 z-5"
                         style={{ left: `${(m.val / Math.max(benchmark.p75*1.3, benchmark.company_value*1.3 || 50))*100}%` }}>
                      <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-[10px] text-gray-500">{m.label}: {m.val}%</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-6 text-center">
                  {language === 'es'
                    ? `Tu empresa se encuentra en el percentil ${benchmark.percentile_position === 'below_p25' ? 'inferior al 25' : benchmark.percentile_position === 'p25_p50' ? '25-50' : benchmark.percentile_position === 'p50_p75' ? '50-75' : 'superior al 75'} de tu industria en rotacion anual`
                    : `Your company is in the ${benchmark.percentile_position === 'below_p25' ? 'below 25th' : benchmark.percentile_position === 'p25_p50' ? '25th-50th' : benchmark.percentile_position === 'p50_p75' ? '50th-75th' : 'above 75th'} percentile of your industry in annual turnover`}
                </p>
              </CardContent>
            </Card>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">{t.tenureBands}</CardTitle>
              </CardHeader>
              <CardContent>
                {tenure?.bands && Object.keys(tenure.bands).length > 0 ? (
                  <div className="space-y-2">
                    {Object.entries(tenure.bands).map(([band, count]) => (
                      <HBar key={band} label={band} value={count} max={demographics?.total_employees || 1} color="#6366f1" />
                    ))}
                  </div>
                ) : <p className="text-gray-400 text-sm">{t.noData}</p>}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">
                  {language === 'es' ? 'Distribucion de Riesgo' : 'Risk Distribution'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {tenure?.risk_distribution && Object.keys(tenure.risk_distribution).length > 0 ? (
                  <div className="space-y-2">
                    {Object.entries(tenure.risk_distribution).map(([level, count]) => (
                      <HBar key={level} label={riskLabels[level] || level} value={count} 
                            max={demographics?.total_employees || 1} color={riskColors[level] || '#9ca3af'} />
                    ))}
                  </div>
                ) : <p className="text-gray-400 text-sm">{t.noData}</p>}
              </CardContent>
            </Card>
          </div>

          {tenure?.score_by_area && tenure.score_by_area.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">
                  {language === 'es' ? 'MEVIA Score por Area' : 'MEVIA Score by Area'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {tenure.score_by_area.map(item => (
                    <HBar key={item.area} label={`${item.area} (${item.count})`} value={Math.round(item.avg_score)} max={100}
                          color={item.avg_score >= 70 ? '#22c55e' : item.avg_score >= 50 ? '#f59e0b' : '#ef4444'} />
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        
        {/* Block 3: Capital Heatmap */}
        <TabsContent value="heatmap" className="space-y-4" data-testid="heatmap-content">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">{t.heatmapTitle}</CardTitle>
              <CardDescription>{t.heatmapDesc}</CardDescription>
            </CardHeader>
            <CardContent>
              {heatmap?.departments && heatmap.departments.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse" data-testid="heatmap-table">
                    <thead>
                      <tr>
                        <th className="text-left px-2 py-1.5 text-xs font-semibold text-gray-600 border border-gray-200 bg-gray-50">
                          {language === 'es' ? 'Depto' : 'Dept'}
                        </th>
                        {Object.keys(capLabels).map(key => (
                          <th key={key} className="px-2 py-1.5 text-xs font-semibold text-gray-600 border border-gray-200 bg-gray-50 text-center">
                            {capLabels[key]}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {heatmap.departments.map(dept => (
                        <tr key={dept.name}>
                          <td className="px-2 py-1.5 text-xs font-medium text-gray-700 border border-gray-200 bg-gray-50 whitespace-nowrap">
                            {dept.name}
                          </td>
                          {Object.keys(capLabels).map(key => (
                            <HeatCell key={key} value={dept.capitals?.[key] || 0} />
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <p className="text-gray-400 text-sm text-center py-6">{t.noData}</p>}
              
              <div className="flex items-center gap-4 mt-4 justify-center text-xs text-gray-500">
                <div className="flex items-center gap-1">
                  <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgb(255, 100, 100)' }} />
                  {t.low}
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgb(178, 178, 100)' }} />
                  {t.medium}
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgb(55, 255, 100)' }} />
                  {t.high}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Block 4: Personality */}
        <TabsContent value="personality" className="space-y-4" data-testid="personality-content">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">{t.personalityTitle}</CardTitle>
              <CardDescription>{t.personalityDesc}</CardDescription>
            </CardHeader>
            <CardContent>
              {personality?.distribution && Object.keys(personality.distribution).length > 0 ? (
                <div className="space-y-2">
                  {Object.entries(personality.distribution)
                    .sort((a, b) => b[1] - a[1])
                    .map(([type, count]) => {
                      const typeInfo = ENNEAGRAM_TYPES[type] || { name: `Type ${type}`, color: '#9ca3af' };
                      return (
                        <HBar key={type} 
                              label={`T${type}: ${typeInfo.name}`} 
                              value={count} 
                              max={personality.total_assessed || 1} 
                              color={typeInfo.color} />
                      );
                    })}
                  {personality.dominant_type && (
                    <div className="mt-4 p-3 bg-indigo-50 rounded-lg flex items-center gap-3">
                      <Brain className="w-5 h-5 text-indigo-600" />
                      <div>
                        <p className="font-medium text-sm">{t.dominantType}: Tipo {personality.dominant_type}</p>
                        <p className="text-xs text-gray-500">
                          {ENNEAGRAM_TYPES[personality.dominant_type]?.name || ''}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ) : <p className="text-gray-400 text-sm text-center py-6">{t.noData}</p>}
            </CardContent>
          </Card>

          {/* By Area with mono-type alert */}
          {personality?.by_area && Object.keys(personality.by_area).length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">
                  {language === 'es' ? 'Personalidad por Area' : 'Personality by Area'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {Object.entries(personality.by_area).map(([area, info]) => (
                    <div key={area} className="border rounded-lg p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium">{area}</span>
                        {info.mono_type_alert && (
                          <Badge variant="destructive" className="text-xs" data-testid={`mono-alert-${area}`}>
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            {language === 'es' ? 'Equipo mono-tipo' : 'Mono-type team'}
                          </Badge>
                        )}
                      </div>
                      <div className="flex gap-1 flex-wrap">
                        {Object.entries(info.types)
                          .sort((a, b) => b[1] - a[1])
                          .map(([type, count]) => (
                            <Badge key={type} variant="outline" className="text-xs"
                                   style={{ borderColor: ENNEAGRAM_TYPES[type]?.color || '#9ca3af' }}>
                              T{type}: {count}
                            </Badge>
                          ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        
        {/* Block 5: Plan Effectiveness */}
        <TabsContent value="plans" className="space-y-4" data-testid="plans-content">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KPICard icon={Target} label={t.totalPlans} value={plans?.total || 0} color="blue" />
            <KPICard icon={CheckCircle2} label={t.completedPlans} value={plans?.completed || 0} color="green" />
            <KPICard icon={Activity} label={t.avgCompletion} 
                     value={plans?.avg_completion ? `${plans.avg_completion.toFixed(0)}%` : '0%'} color="purple" />
            <KPICard icon={TrendingUp} label={t.impactScore} 
                     value={plans?.impact_delta ? `+${plans.impact_delta.toFixed(1)}` : 'N/A'} color="teal" />
          </div>
          
          {plans?.by_capital && Object.keys(plans.by_capital).length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">
                  {language === 'es' ? 'Planes por Capital' : 'Plans by Capital'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-1">
                  {Object.entries(plans.by_capital)
                    .sort((a, b) => b[1] - a[1])
                    .map(([cap, count]) => (
                      <HBar key={cap} label={capLabels[cap] || cap} value={count} max={plans.total || 1} color="#14b8a6" />
                    ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        
        {/* Block 6: Retention ROI */}
        <TabsContent value="roi" className="space-y-4" data-testid="roi-content">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KPICard icon={DollarSign} label={t.estimatedSavings} 
                     value={roi?.estimated_savings ? `$${(roi.estimated_savings / 1000).toFixed(0)}k` : '$0'} color="green" />
            <KPICard icon={DollarSign} label={t.costPerHire} 
                     value={roi?.cost_per_hire ? `$${(roi.cost_per_hire / 1000).toFixed(0)}k` : 'N/A'} color="red" />
            <KPICard icon={Users} label={t.retainedEmployees} 
                     value={roi?.retained_employees || 0} color="blue" />
            <KPICard icon={Award} label={t.roiMultiplier} 
                     value={roi?.roi_multiplier ? `${roi.roi_multiplier.toFixed(1)}x` : 'N/A'} color="purple" />
          </div>
          
          <Card>
            <CardContent className="pt-6">
              <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-lg p-6 text-center">
                <Award className="w-10 h-10 text-teal-600 mx-auto mb-3" />
                <p className="text-lg font-bold text-gray-900">
                  {language === 'es' ? 'Valor estimado de retencion' : 'Estimated retention value'}
                </p>
                <p className="text-3xl font-bold text-teal-600 mt-2">
                  ${roi?.total_retention_value ? (roi.total_retention_value / 1000).toFixed(0) : 0}k MXN
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {language === 'es' 
                    ? 'Basado en costos de reemplazo evitados por retencion mejorada'
                    : 'Based on replacement costs avoided through improved retention'}
                </p>
                {roi?.salary_source === 'default' && (
                  <p className="text-xs text-amber-600 mt-2">
                    {language === 'es'
                      ? 'Usando salario promedio por defecto ($25,000 MXN). Registra salarios reales en los expedientes para mayor precision.'
                      : 'Using default average salary ($25,000 MXN). Register real salaries in profiles for better accuracy.'}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Block 7: Pulse Trends (M13 Fase 6) */}
        <TabsContent value="pulse" className="space-y-4" data-testid="pulse-trends-content">
          {!pulseTrends || !pulseTrends.has_data ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Activity className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">{t.noPulseData}</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Summary KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <KPICard icon={Activity} label={t.overallDelta}
                  value={`${pulseTrends.overall_delta > 0 ? '+' : ''}${pulseTrends.overall_delta}`}
                  color={pulseTrends.improved ? 'green' : 'red'}
                />
                <KPICard icon={BarChart3} label={t.ipmTrend}
                  value={`${pulseTrends.total_periods} ${t.periods}`}
                  color="blue"
                />
                <KPICard icon={TrendingUp} label={pulseTrends.improved ? t.improved : t.declined}
                  value={pulseTrends.ipm_trend?.length > 0 ? pulseTrends.ipm_trend[pulseTrends.ipm_trend.length - 1].ipm?.toFixed(1) : 'N/A'}
                  color={pulseTrends.improved ? 'green' : 'red'}
                />
              </div>

              {/* IPM Trend Chart */}
              {pulseTrends.ipm_trend?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">{t.ipmTrend}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {pulseTrends.ipm_trend.map((point, i) => {
                        const maxIpm = Math.max(...pulseTrends.ipm_trend.map(p => p.ipm), 100);
                        const pct = (point.ipm / maxIpm) * 100;
                        return (
                          <div key={i} className="flex items-center gap-3">
                            <span className="text-xs text-gray-500 w-40 truncate">{point.name}</span>
                            <div className="flex-1 bg-gray-100 rounded-full h-5 relative overflow-hidden">
                              <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: '#0d9488' }} />
                            </div>
                            <span className="text-xs font-semibold w-10 text-right">{point.ipm?.toFixed(1)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Participation Trend */}
              {pulseTrends.participation_trend?.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">{t.participationTrend}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {pulseTrends.participation_trend.map((pt, i) => (
                        <div key={i} className="flex items-center gap-3">
                          <span className="text-xs text-gray-500 w-40 truncate">{pt.name}</span>
                          <div className="flex-1 bg-gray-100 rounded-full h-5 relative overflow-hidden">
                            <div className="h-full rounded-full transition-all" style={{ width: `${pt.rate}%`, backgroundColor: '#6366f1' }} />
                          </div>
                          <span className="text-xs font-semibold w-20 text-right">{pt.responded}/{pt.targeted} ({pt.rate?.toFixed(0)}%)</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Dimension Movements */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {pulseTrends.most_improved?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-green-600 flex items-center gap-1.5">
                        <TrendingDown className="w-4 h-4" /> {t.mostImproved}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {pulseTrends.most_improved.map((m, i) => (
                        <div key={i} className="flex justify-between items-center py-1.5 border-b last:border-0">
                          <span className="text-sm text-gray-700">{m.label}</span>
                          <div className="text-right">
                            <span className="text-xs text-gray-400">{m.first} → {m.last}</span>
                            <Badge variant="outline" className="ml-2 text-xs text-green-600 border-green-200">
                              {m.delta > 0 ? '+' : ''}{m.delta}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}
                {pulseTrends.most_declined?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-red-600 flex items-center gap-1.5">
                        <TrendingUp className="w-4 h-4" /> {t.mostDeclined}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {pulseTrends.most_declined.map((m, i) => (
                        <div key={i} className="flex justify-between items-center py-1.5 border-b last:border-0">
                          <span className="text-sm text-gray-700">{m.label}</span>
                          <div className="text-right">
                            <span className="text-xs text-gray-400">{m.first} → {m.last}</span>
                            <Badge variant="outline" className="ml-2 text-xs text-red-600 border-red-200">
                              {m.delta > 0 ? '+' : ''}{m.delta}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}
              </div>
            </>
          )}
        </TabsContent>

        {/* Block 8: NOM-035 × Cultura */}
        {showNomCultura && (
          <TabsContent value="nomCultura" className="space-y-4" data-testid="nom-cultura-content">
            {nomCulturaData ? (
              <>
                {/* KPI Cards */}
                <div className="grid grid-cols-2 gap-4">
                  <KPICard
                    icon={ShieldAlert}
                    label={t.nomCulturaDoubleSignals}
                    value={nomCulturaData.seniales_dobles_count || 0}
                    color={nomCulturaData.seniales_dobles_count > 0 ? 'red' : 'green'}
                  />
                  <KPICard
                    icon={BarChart3}
                    label={t.nomCulturaDomains}
                    value={`${nomCulturaData.total_dominios || 0} / 9`}
                    color="blue"
                  />
                </div>

                {/* Correlation Table */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">{t.nomCulturaTitle}</CardTitle>
                    <CardDescription>{t.nomCulturaDesc}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border-collapse" data-testid="nom-cultura-table">
                        <thead>
                          <tr>
                            <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600 border border-gray-200 bg-gray-50">
                              #
                            </th>
                            <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600 border border-gray-200 bg-gray-50">
                              {language === 'es' ? 'Dominio NOM-035' : 'NOM-035 Domain'}
                            </th>
                            <th className="text-center px-3 py-2 text-xs font-semibold text-gray-600 border border-gray-200 bg-gray-50">
                              {t.nomCulturaRisk}
                            </th>
                            <th className="text-left px-3 py-2 text-xs font-semibold text-gray-600 border border-gray-200 bg-gray-50">
                              {t.nomCulturaDimensions}
                            </th>
                            <th className="text-center px-3 py-2 text-xs font-semibold text-gray-600 border border-gray-200 bg-gray-50">
                              {t.nomCulturaGap}
                            </th>
                            <th className="text-center px-3 py-2 text-xs font-semibold text-gray-600 border border-gray-200 bg-gray-50">
                              {t.nomCulturaDoubleSignalTag}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {nomCulturaData.dominios?.map((dom) => {
                            const riskColors = {
                              'NULO': 'bg-green-50 text-green-700',
                              'BAJO': 'bg-blue-50 text-blue-700',
                              'MEDIO': 'bg-yellow-50 text-yellow-700',
                              'ALTO': 'bg-orange-50 text-orange-700',
                              'MUY_ALTO': 'bg-red-50 text-red-700',
                              'SIN_DATOS': 'bg-gray-50 text-gray-400',
                            };
                            const riskClass = riskColors[dom.nivel_riesgo] || riskColors['SIN_DATOS'];
                            const maxGap = dom.gaps ? Math.max(...Object.values(dom.gaps).map(Math.abs)) : 0;

                            return (
                              <tr key={dom.num_dominio} className={dom.senial_doble ? 'bg-red-50/50' : ''}>
                                <td className="px-3 py-2 text-xs text-gray-500 border border-gray-200 text-center font-mono">
                                  D{dom.num_dominio}
                                </td>
                                <td className="px-3 py-2 text-xs font-medium text-gray-800 border border-gray-200">
                                  {dom.nombre}
                                </td>
                                <td className="px-3 py-2 text-center border border-gray-200">
                                  <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${riskClass}`}>
                                    {dom.nivel_riesgo}
                                  </span>
                                </td>
                                <td className="px-3 py-2 text-xs text-gray-700 border border-gray-200">
                                  {dom.dimensiones?.join(', ')}
                                </td>
                                <td className="px-3 py-2 text-center border border-gray-200">
                                  {dom.gaps && Object.keys(dom.gaps).length > 0 ? (
                                    <span className={`text-xs font-semibold ${maxGap > 8 ? 'text-red-600' : maxGap > 4 ? 'text-yellow-600' : 'text-green-600'}`}>
                                      {maxGap > 0 ? `+${maxGap.toFixed(1)}` : maxGap.toFixed(1)}
                                    </span>
                                  ) : <span className="text-xs text-gray-400">-</span>}
                                </td>
                                <td className="px-3 py-2 text-center border border-gray-200">
                                  {dom.senial_doble ? (
                                    <Badge variant="destructive" className="text-xs" data-testid={`double-signal-d${dom.num_dominio}`}>
                                      <AlertTriangle className="w-3 h-3 mr-1" />
                                      {t.nomCulturaDoubleSignalTag}
                                    </Badge>
                                  ) : <span className="text-xs text-gray-300">-</span>}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>

                {/* Executive Summary */}
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Target className="w-4 h-4" />
                      {t.nomCulturaExecutiveSummary}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-700 leading-relaxed" data-testid="nom-cultura-summary">
                      {language === 'es' ? nomCulturaData.resumen_ejecutivo_es : nomCulturaData.resumen_ejecutivo_en}
                    </p>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <ShieldAlert className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm" data-testid="nom-cultura-empty">{t.nomCulturaNoData}</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
